import Box from '@mui/material/Box/Box';
import { styled } from '@mui/material/styles';

// ─── Decorator circles ────────────────────────────────────────────────────────

export const HeroDecorCircleTop = styled(Box)({
  position: 'absolute',
  top: -96,
  right: -96,
  height: 288,
  width: 288,
  borderRadius: '50%',
  backgroundColor: 'rgba(255,255,255,0.1)',
  filter: 'blur(64px)',
  pointerEvents: 'none',
});

export const HeroDecorCircleBottom = styled(Box)({
  position: 'absolute',
  bottom: -96,
  left: '33%',
  height: 256,
  width: 256,
  borderRadius: '50%',
  backgroundColor: 'rgba(255,255,255,0.05)',
  filter: 'blur(64px)',
  pointerEvents: 'none',
});

// ─── Layout primitives ────────────────────────────────────────────────────────

export const HeroContent = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
});

export const HeroSubtitle = styled(Box)({
  marginTop: 2,
  fontSize: '0.875rem',
  color: 'rgba(255,255,255,0.6)',
});

// ─── Stat card primitives ─────────────────────────────────────────────────────

export const HeroStatsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(1, 1fr)',
  gap: 12,
  marginTop: 16,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}));

export const HeroStatCard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  borderRadius: 16,
  backgroundColor: 'rgba(255,255,255,0.14)',
  backdropFilter: 'blur(4px)',
  padding: '16px 20px',
});

export const HeroStatLabel = styled('span')({
  fontSize: '0.6875rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
  color: 'rgba(255,255,255,0.5)',
});

export const HeroStatValueRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const HeroStatDot = styled('span')<{ dotcolor: string }>(({ dotcolor }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: dotcolor,
  flexShrink: 0,
}));

export const HeroStatValue = styled('span')({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#fff',
  fontVariantNumeric: 'tabular-nums',
});

export const HeroStatHelperText = styled('span')({
  fontSize: '0.6875rem',
  color: 'rgba(255,255,255,0.4)',
});

export const HeroActionButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  height: 40,
  padding: theme.spacing(0, 2.5),
  borderRadius: 10,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.875rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  color: theme.palette.text.dark,
  backgroundColor: '#fff',
  boxShadow: '0 2px 4px rgba(0,0,0,0.10), 0 4px 6px rgba(0,0,0,0.10)',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.92)',
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
}));

// ─── Shell ────────────────────────────────────────────────────────────────────

export const HeroRoot = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'heroHeight' && prop !== 'heroBorderRadius' && prop !== 'heroPadding',
})<{
  heroHeight?: number | string;
  heroBorderRadius?: number | string;
  heroPadding?: number | string;
}>(({ theme, heroHeight, heroBorderRadius, heroPadding }) => ({
  background:
    'linear-gradient(85deg, rgb(var(--hero-from-rgb)) 0%, rgb(var(--hero-via-rgb)) 40%, rgb(var(--hero-to-rgb)) 100%)',
  height: heroHeight ?? 'auto',
  borderRadius: heroBorderRadius ?? 28,
  padding: heroPadding ?? 24,
  color: '#fff',
  position: 'relative',
  overflow: 'hidden',
  flexShrink: 0,
}));
